import * as Sentry from '@sentry/vue'
import FloatingVue from 'floating-vue'
import { createApp } from 'vue'
import App from './App.vue'
import { useFeedbackWidget } from './composables/useFeedbackWidget'
import { vEdit } from './directives/edit'
import { vFeature } from './directives/feature'
import { vFocusTrap } from './directives/focusTrap'
import i18n from './i18n'
import router from './router'
import { API_BASE } from './utils/api'
import { ApiError } from './utils/apiClient'
import { floatingVueOptions } from './utils/floatingVue'
import { redactHealthBreadcrumb } from './utils/redactBreadcrumb'
import { redactSentryRequest } from './utils/redactSentryRequest'
import { sentryReplayEnabled, sentryReplayOptions } from './utils/sentryPrivacy'

import 'floating-vue/dist/style.css'
import './styles/_tokens.scss'
import './styles/_content.scss'
import './styles/_mobile-page.scss'
import './styles/_tooltip.scss'
import './styles/_field-error.scss'
import './styles/_field-hint.scss'
import './styles/_field-required.scss'
import './styles/_label-mono.scss'
import './styles/_kicker.scss'
import './styles/_ab-edit.scss'
import './styles/_feedback-widget.scss'

const app = createApp(App)

app.directive('focus-trap', vFocusTrap)
app.directive('feature', vFeature)
app.directive('edit', vEdit)

const sentryDsn = import.meta.env.VITE_SENTRY_DSN
if (sentryDsn) {
  const replayOptions = sentryReplayOptions(import.meta.env)
  const integrations = [
    Sentry.browserTracingIntegration(),
    // Self-serve "report a problem" widget (#3316): a prospective
    // operator stuck on signup had no way to tell us except phoning in.
    // Colors are set via the typed theme below; font-family and
    // border-radius aren't in that typed API but ARE real CSS custom
    // properties on the widget's shadow host (see _feedback-widget.scss).
    // Where/when it shows is scoped by useFeedbackWidget (main app root),
    // not global — see that file for why.
    Sentry.feedbackIntegration({
      id: 'stemne-feedback-widget',
      // Scoped manually by useFeedbackWidget (main app root, wired below)
      // — marketing + admin only, re-evaluated on every navigation. A
      // second auto-injected instance here would double up.
      autoInject: false,
      showBranding: false,
      colorScheme: 'light',
      themeLight: {
        background: '#FBF8F1',
        foreground: '#1C1915',
        accentBackground: '#586649',
        accentForeground: '#F7F2E8',
        errorColor: '#8A3A24',
        outline: '2px solid #586649',
      },
      triggerLabel: 'Gi tilbakemelding',
      triggerAriaLabel: 'Gi tilbakemelding',
      formTitle: 'Gi tilbakemelding',
      nameLabel: 'Navn',
      namePlaceholder: 'Fornavn Etternavn',
      emailLabel: 'E-post',
      emailPlaceholder: 'din@epost.no',
      isRequiredLabel: '(påkrevd)',
      messageLabel: 'Hva skjedde?',
      messagePlaceholder: 'Fortell oss hva som skjedde, eller hva du tenker på.',
      addScreenshotButtonLabel: 'Legg til skjermbilde',
      removeScreenshotButtonLabel: 'Fjern skjermbilde',
      highlightToolText: 'Merk',
      hideToolText: 'Skjul',
      removeHighlightText: 'Fjern merking',
      cancelButtonLabel: 'Avbryt',
      submitButtonLabel: 'Send',
      confirmButtonLabel: 'Bekreft',
      successMessageText: 'Takk for tilbakemeldingen.',
    }),
  ]
  if (sentryReplayEnabled(replayOptions)) {
    integrations.push(
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
      }),
    )
  }

  Sentry.init({
    app,
    dsn: sentryDsn,
    // Reuse API_BASE so the tunnel falls back to https://api.stemne.no
    // in prod when VITE_API_URL is missing — otherwise the relative path
    // resolves to {slug}.stemne.no/api/sentry-tunnel and CF Pages 405s.
    tunnel: `${API_BASE}/api/sentry-tunnel`,
    // MODE alone can't tell QA and prod apart: both build via plain
    // `vite build` (CF Pages runs the same command for every custom
    // domain on the shared stemne-frontend Pages project), so MODE is
    // "production" regardless of target. VITE_SENTRY_ENVIRONMENT is an
    // explicit per-target CF Pages build var (like VITE_PLATFORM_HOST/
    // VITE_API_URL already are) — set it to "qa" on the QA project,
    // leave it unset on prod so MODE's default keeps working there.
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE,
    release: import.meta.env.VITE_APP_VERSION,
    // v11 replaced `sendDefaultPii: false` with `dataCollection`, whose
    // defaults are all collect-on. Opt out explicitly: no user inference,
    // cookies, bodies, or query params (magic-link `?token=` lives there).
    // User-Agent stays so Sentry can still tag browser/OS.
    dataCollection: {
      userInfo: false,
      cookies: false,
      httpHeaders: { allow: ['User-Agent'] },
      httpBodies: [],
      urlQueryParams: false,
    },
    integrations,
    // Session Replay is DOM/session tracking. For Stemne's health-adjacent
    // surfaces it must be an explicit build-time opt-in after a privacy
    // decision, not a default side effect of wiring Sentry. Feedback-triggered
    // Replay may install the integration with both rates at 0: it only buffers
    // once the user opens the feedback dialog, and only flushes on submit.
    // Sentry's Replay integration also gets strict masking above whenever it is
    // enabled.
    replaysSessionSampleRate: replayOptions.rates.session,
    replaysOnErrorSampleRate: replayOptions.rates.onError,
    // 10% of transactions captured for performance — well below quota
    // burn at our scale. Bump if we need higher fidelity later.
    tracesSampleRate: 0.1,
    // Drop noise injected by native in-app browsers (Facebook/Messenger
    // and Instagram WKWebView, etc.). Those hosts inject their own JS into
    // the page and call native message-handler bridges on
    // `window.webkit.messageHandlers`. When the host build hasn't
    // registered the handler the bridge is undefined and the call throws a
    // TypeError inside their injected code — nothing in Stemne can prevent
    // or fix it. Two known variants:
    //   - Facebook/Messenger: `setupIosCallbackHandler` calls
    //     `window.webkit.messageHandlers.navigationPerformanceLoggerWithReply.postMessage`.
    //   - Instagram: `sendDataToNative` evaluates `window.webkit.messageHandlers`
    //     directly, so the message is "undefined is not an object (evaluating
    //     'window.webkit.messageHandlers')" with no `.postMessage` — the old
    //     postMessage-only regex missed it (STEMNE-FRONTEND-13, 287 events).
    // The stack frame resolves to our raw HTML document, so it masquerades
    // as an app error. See issue #124230764.
    ignoreErrors: [
      /webkit\.messageHandlers/,
      'navigationPerformanceLoggerWithReply',
      'setupIosCallbackHandler',
      'sendDataToNative',
    ],
    // Redact health-adjacent request URLs out of fetch/XHR breadcrumbs
    // before they are recorded. See redactBreadcrumb.ts for why this is a
    // separate hook from the beforeSend scrubbing below (that one matches
    // breadcrumb data KEY NAMES and never inspects the url value).
    beforeBreadcrumb: redactHealthBreadcrumb,
    beforeSend(event, hint) {
      // Drop 4xx ApiErrors before transport. They're domain/validation
      // errors, not bugs — same policy apiClient.ts uses for its
      // manual captureException path. The leak we're closing here is
      // the auto-capture from Vue's global error handler: when
      // apiClient redirects on 401 and throws ApiError(401, "session
      // expired") to halt the call chain, the throw bubbles past any
      // try/catch into Vue's global handler, which auto-ships to
      // Sentry. That generated the "session expired" issue (#190).
      const original = hint?.originalException
      if (original instanceof ApiError && original.isDomainError) {
        return null
      }

      // Scrub sensitive data from breadcrumbs
      if (event.breadcrumbs) {
        for (const crumb of event.breadcrumbs) {
          if (crumb.data) {
            for (const key of Object.keys(crumb.data)) {
              if (/password|totp|token|csrf|secret/i.test(key)) {
                crumb.data[key] = '[scrubbed]'
              }
            }
          }
        }
      }
      // Scrub auth headers from request data
      if (event.request?.headers) {
        for (const key of Object.keys(event.request.headers)) {
          if (/cookie|authorization|x-csrf-token/i.test(key)) {
            event.request.headers[key] = '[scrubbed]'
          }
        }
      }
      // Scrub credentials out of the PAGE url. The SDK fills
      // `event.request.url` from window.location.href, and Stemne puts a
      // Whereby room capability and a booking magic token in the query on
      // /videorom. The key-name scrubbing above never looks at it — see
      // redactSentryRequest.ts.
      redactSentryRequest(event.request)
      return event
    },
  })
}

app.use(i18n)

// The i18n instance is created with the resolved locale (saved choice →
// tenant's remembered brand language on end-customer surfaces → browser
// language → nb, see ./i18n and ./i18n/locale) so the first paint is
// already in the final language. Here we only mirror it onto <html lang>.
// When /api/public/tenant resolves, useTenant applies the live brand
// locale via ./i18n/tenantLocale and refreshes the remembered value.
document.documentElement.lang = i18n.global.locale.value

app.use(router)
useFeedbackWidget(router)

// Surface unhandled component errors as the 500 scene, and pair the
// navigation with Sentry's event id so support can paste it into the
// Sentry search box and land on the exact event. Why capture+push here
// instead of reading Sentry.lastEventId() on the destination: the latter
// is a tab-global and would return stale ids when the user lands on /500
// for unrelated reasons (manual URL, direct link). Binding the id to the
// capture keeps the round-trip honest.
app.config.errorHandler = (err) => {
  // Match the beforeSend filter above: domain-shaped 4xx ApiErrors
  // (notably the 401 "session expired" rethrow apiClient uses to halt
  // call chains) are not bugs — they shouldn't ship to Sentry and they
  // shouldn't navigate the user away from whatever auth-redirect flow
  // they're already in.
  if (err instanceof ApiError && err.isDomainError) {
    return
  }
  // Avoid recursing if ErrorPageView itself throws during render.
  if (router.currentRoute.value.name === 'error-server') {
    return
  }
  const eventId = Sentry.captureException(err)
  router.push({ name: 'error-server', state: { eventId } })
}
app.use(FloatingVue, floatingVueOptions)
app.mount('#app')
